import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { Loader2, AlertCircle, FileText, LayoutDashboard, BarChart3 } from 'lucide-react'
import { loadDashboardObject, loadVisualObject, loadPageObject } from '@/api/designers'
import { useObjectVersion } from '@/store/objectVersionStore'
import type { EmbedCaptureTemplate, EmbedConfig, EmbedFilter, PageInput } from './types'
import { normalizeEmbedMode } from '@twasta/page-schema'
import type { PageModel } from '@twasta/page-schema'
import type { ColumnDef, SqlModel, TransactionModel } from '../transaction/types'
import { FormPreviewView, GridPreviewView, formatColumnLabel, isMandatory } from '../transaction/PreviewView'
import EmbedListInterfaceHost from './EmbedListInterfaceHost'
import EmbedCaptureSingleClassicPreview from './EmbedCaptureSingleClassicPreview'
import EmbedCaptureTabPreview from './EmbedCaptureTabPreview'
import EmbedCaptureAccordionPreview from './EmbedCaptureAccordionPreview'
import EmbedWizardBody from './EmbedWizardBody'
import { embedLoadModel } from '@/livepreview/embedDataApi'
import type { DataAccess } from '@/pages/workbench/smartpage-preview/dataBinding'
import SmartPageRuntime from '@/pages/workbench/smartpage-preview/SmartPageRuntime'
import { dashboardTitleLoader } from '../dashboard/dashboardTitle'
import toast from 'react-hot-toast'
import {
  isDocumentVisual,
  InsightVisual, readAutoRefreshSeconds, useAutoRefresh, UnderlyingDataModal,
  dashGridContainerStyle, dashTileStyle, dashGridHeightPx,
  resolveDrillThrough, drillTargetType, drillMappedValues, useLastPointer, DrillChooser,
  useDrillTargetTitles, readDataError,
} from '@twasta/visual-engine'
import {
  fetchVisualData, fetchDashboardData, makeVisualCriteriaRuntime,
  type DashboardDataResult,
} from '@/api/visualData'
import {
  QuickFilterBar, QuickFilterToggle, TileFilterBadge,
  chipsToFilterExpression, mergeQuickFilterPicks, removeQuickFilterValue,
  quickFilterKey, quickFilterReach, tileQuickFilterIndication,
  REASON_REPORT_TILE, REASON_TILE_ERROR,
  useVisualModels, MODEL_FALLBACK_REASON_TEXT,
} from '@twasta/visual-engine'
import type { VisualDataPayload, UnderlyingPayload, SelectInfo } from '@twasta/visual-engine'
import type {
  QuickFilterChip, QuickFilterPick, TileQuickFilterStatus, FilterResult,
} from '@twasta/visual-engine'
import { VisualCriteriaForm, type VisualCriteriaPromptVar } from '@twasta/page-runtime/txn'
import ReportEmbedBody from '../report/ReportEmbedBody'
import { VisualDocumentPreview } from '../visual/VisualDocumentPreview'
import { substituteVars } from './actions/substitute'

interface Props {
  project: string
  embed: EmbedConfig
  inputs: PageInput[]
  inputValues: Record<string, unknown>
  // Plumbed down from PageDesignerPage so an embedded list's row
  // selection can update the smart page's input values. Optional —
  // some callers (capture/document mode) don't need it.
  onSetInput?: (name: string, value: unknown) => void
  // Fired after a successful capture/integrate save. Used by the modal
  // presentation host to show a confirmation and auto-close the dialog.
  onSaved?: () => void
  // Routes the embed's transaction model + CRUD calls to the site-txn
  // endpoints in site mode, so the form works for end-users on a published
  // page. Threaded from the runtime (RuntimeBlockHost).
  dataAccess?: DataAccess
  // Which surface this embed renders on. 'design' = the page-designer
  // canvas (BlockNode); 'runtime' = any runtime surface (in-app live
  // preview, published run) via RuntimeBlockHost's EmbedBody. Drives the
  // runtime interface switcher: it shows only at runtime (and only when the
  // embed's allowInterfaceSwitch flag is on), never on the canvas where the
  // interface is chosen in the side panel.
  surface?: 'design' | 'runtime'
  // The block's explicit height (CSS length, e.g. "300px"), when the author set
  // one in the Size & Position panel. When provided, a visual / dashboard embed
  // FILLS this height; when absent it sizes to content (capped at one screen).
  bodyHeight?: string
}

const OP_LABEL: Record<string, string> = {
  eq: '=', neq: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤',
  between: 'between', contains: 'contains', starts_with: 'starts with',
}

function resolveFilterDisplay(f: EmbedFilter, inputValues: Record<string, unknown>): string {
  const fmt = (ref?: { source: string; name?: string; value?: string }) => {
    if (!ref) return '∅'
    if (ref.source === 'page_input') {
      const v = ref.name ? inputValues[ref.name] : undefined
      if (v === undefined || v === null || v === '') return `[${ref.name || '?'}]`
      return typeof v === 'object' ? JSON.stringify(v) : String(v)
    }
    return ref.value || '∅'
  }
  if (f.op === 'between') {
    return `${f.column || '?'} ${OP_LABEL[f.op]} ${fmt(f.from)} … ${fmt(f.to)}`
  }
  return `${f.column || '?'} ${OP_LABEL[f.op]} ${fmt(f.value)}`
}

// Resolve an embed's argument mappings into a flat params object for a
// visual / dashboard data fetch:
//   • page_input → the current page-variable value
//   • static     → the literal (with {{var}} substitution)
// Empty / unset values are dropped so the visual falls back to its own
// default (and any still-required prompt surfaces as "needs input"). Also the
// launch inputs of an embedded wizard (EmbedWizard).
function resolveVisualArgs(
  args: EmbedConfig['args'],
  inputValues: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const a of args || []) {
    if (!a.target) continue
    let v: unknown
    if (a.source?.source === 'page_input') {
      v = a.source.name ? inputValues[a.source.name] : undefined
    } else {
      const raw = a.source?.value
      v = typeof raw === 'string' ? substituteVars(raw, inputValues) : raw
    }
    if (v === undefined || v === null || v === '') continue
    out[a.target] = v
  }
  return out
}

// Build the onSelect handler for an embedded visual: when the author has
// configured selection variables, a row/card/node click writes the chosen
// key column into selectedIdVar and the full row into selectedDataVar.
// Returns undefined when nothing is configured (so the visual stays
// non-interactive — no pointer cursor) or when no setter is available
// (the design canvas, where selection has no page-var sink).
function makeVisualSelectHandler(
  embed: EmbedConfig,
  onSetInput?: (name: string, value: unknown) => void,
): ((row: Record<string, unknown>) => void) | undefined {
  if (!onSetInput) return undefined
  if (!embed.selectedIdVar && !embed.selectedDataVar) return undefined
  return (row: Record<string, unknown>) => {
    if (embed.selectedIdVar) {
      const key = embed.selectedKeyColumn
      const val = key ? embedLookupField(row, key) : Object.values(row)[0]
      onSetInput(embed.selectedIdVar, val ?? '')
    }
    if (embed.selectedDataVar) {
      onSetInput(embed.selectedDataVar, row)
    }
  }
}

export default function EmbedPreview({ project, embed, inputs, inputValues, onSetInput, onSaved, dataAccess, surface = 'design', bodyHeight }: Props) {
  // The runtime interface switcher is shown only on runtime surfaces, and
  // only when the designer left interface switching enabled (default on).
  const showSwitcher = surface === 'runtime' && embed?.allowInterfaceSwitch !== false
  // Defensive: an embed block can exist without a configured target
  // (e.g. an unfinished block from generation or a hand-edited model).
  // Render an inert placeholder instead of crashing the whole page when
  // `embed` is missing or has no obj_name.
  if (!embed || !embed.obj_name) {
    return (
      <div style={{
        padding: '1rem 1.25rem', border: '1px dashed var(--pg-border, #cbd5e1)',
        borderRadius: 8, color: 'var(--pg-text-secondary, #64748b)',
        fontSize: 14, background: 'var(--pg-surface, #f8fafc)',
      }}>
        Embedded object is not configured yet.
      </div>
    )
  }
  const objType = embed.obj_type || 'T'

  // Dashboard / visual previews are lighter than the full transaction
  // form/grid render — they show a banner with metadata. The full chart
  // render happens at runtime; we don't reproduce the rendering pipeline
  // inside the page designer.
  if (objType === 'D' || objType === 'V') {
    return <DashboardOrVisualPreview project={project} embed={embed} inputValues={inputValues} onSetInput={onSetInput} bodyHeight={bodyHeight} />
  }

  // Report (R): the shared ReportViewer (criteria bar → HTML preview / export)
  // with the embed's args bound onto the report's prompt params — same binding
  // model as a visual embed.
  if (objType === 'R') {
    return <ReportEmbedPreview project={project} embed={embed} inputValues={inputValues} bodyHeight={bodyHeight} />
  }

  // Transaction Wizard (U): the shared runner, its launch inputs mapped from
  // the page like a report's params (EmbedWizardBody picks the transport —
  // the website's site-txn doors in site mode, /livepreview elsewhere).
  if (objType === 'U') {
    return (
      <EmbedWizardBody
        project={project}
        embed={embed}
        inputs={resolveVisualArgs(embed.args, inputValues)}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        onSaved={onSaved}
        bodyHeight={bodyHeight}
      />
    )
  }

  // Smart Page (S): render another page inline. Its sections/blocks render
  // through a nested SmartPageRuntime; the embed's `args` map this page's
  // input values onto the embedded page's PageInput names. A depth/cycle
  // guard prevents infinite recursion (A→B→A or runaway nesting).
  if (objType === 'S') {
    return (
      <EmbeddedSmartPage
        project={project}
        embed={embed}
        inputValues={inputValues}
        dataAccess={dataAccess}
      />
    )
  }

  // T-list realtime: render the actual live preview (deploys schema,
  // shows real rows with CRUD + variable-driven filters + selection
  // binding) instead of the structural mock. Capture-mode and
  // add/edit forms still render the structural preview below.
  //
  // Wrapped in EmbedListInterfaceHost so the viewer gets a runtime "View"
  // switcher (List / Multi-tabular entry / Form input) in the toolbar —
  // ephemeral, never mutates the saved embed.
  const renderMode = embed.renderMode || 'realtime'
  // Canonical access mode — decides which surface + which affordances. Browse
  // modes (full / edit) render the realtime list; single_add / multi_add are
  // entry-only surfaces even in realtime.
  const accessMode = normalizeEmbedMode(embed)
  if (objType === 'T' && renderMode === 'realtime'
      && (accessMode === 'full' || accessMode === 'edit')) {
    return (
      <EmbedListInterfaceHost
        project={project}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        onSaved={onSaved}
        showSwitcher={showSwitcher}
        accessMode={accessMode}
        bodyHeight={bodyHeight}
      />
    )
  }
  // Realtime single_add → a functional single-record capture form (replaces the
  // old structural add mock). Realtime multi_add → the list host's entry views.
  if (objType === 'T' && renderMode === 'realtime' && accessMode === 'single_add') {
    return (
      <EmbedCaptureSingleClassicPreview
        project={project}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        onSaved={onSaved}
      />
    )
  }
  if (objType === 'T' && renderMode === 'realtime' && accessMode === 'multi_add') {
    return (
      <EmbedListInterfaceHost
        project={project}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        onSaved={onSaved}
        showSwitcher={showSwitcher}
        accessMode={accessMode}
        bodyHeight={bodyHeight}
      />
    )
  }

  // Capture / integrate with the 'multi-form-input' or 'multi-tabular'
  // template — both are list-level interfaces over the same transaction
  // (batch entry backed by the live-preview executor). Route them through
  // EmbedListInterfaceHost too so the viewer gets the same runtime View
  // switcher; the host starts on the saved template and can swap to the
  // list / other entry interface.
  if (objType === 'T' && (renderMode === 'capture' || renderMode === 'integrate')
      && (embed.captureTemplate === 'multi-form-input'
          || embed.captureTemplate === 'multi-tabular'
          || embed.captureTemplate === 'multi-compact')) {
    return (
      <EmbedListInterfaceHost
        project={project}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        onSaved={onSaved}
        showSwitcher={showSwitcher}
        accessMode={accessMode}
        bodyHeight={bodyHeight}
      />
    )
  }

  // 'single-classic' — one record at a time, header form + detail
  // grids stacked. Selection variables bind to the active record. This is
  // also the DEFAULT template: an unset captureTemplate (e.g. a capture
  // embed authored without touching the template dropdown) lands here
  // instead of falling through to the structural mock below.
  if (objType === 'T' && (renderMode === 'capture' || renderMode === 'integrate')
      && (embed.captureTemplate || 'single-classic') === 'single-classic') {
    return (
      <EmbedCaptureSingleClassicPreview
        project={project}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
        onSaved={onSaved}
      />
    )
  }

  // 'multi-tab' — tab strip across records; active tab shows the
  // record's full form.
  if (objType === 'T' && (renderMode === 'capture' || renderMode === 'integrate')
      && embed.captureTemplate === 'multi-tab') {
    return (
      <EmbedCaptureTabPreview
        project={project}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
      />
    )
  }

  // 'multi-accordion' — vertical accordion of records; expanded
  // section shows the record's full form.
  if (objType === 'T' && (renderMode === 'capture' || renderMode === 'integrate')
      && embed.captureTemplate === 'multi-accordion') {
    return (
      <EmbedCaptureAccordionPreview
        project={project}
        embed={embed}
        inputs={inputs}
        inputValues={inputValues}
        dataAccess={dataAccess}
        onSetInput={onSetInput}
      />
    )
  }

  // ── Transaction (T) preview — original behaviour ─────────
  return <TransactionPreview project={project} embed={embed} inputValues={inputValues} dataAccess={dataAccess} />
}

// ─── Embedded smart page (obj_type 'S') ───────────────────────────
// Tracks the chain of embedded page obj_names + nesting depth so a page
// that (transitively) embeds itself, or nests too deeply, renders a
// notice instead of recursing forever. Same module instance is shared by
// the nested EmbedPreview (SmartPageRuntime → RuntimeBlockHost →
// EmbedPreview), so the context value threads down naturally.
const MAX_EMBED_DEPTH = 5
interface EmbedDepthState { depth: number; chain: string[] }
const EmbedDepthContext = createContext<EmbedDepthState>({ depth: 0, chain: [] })

function EmbeddedSmartPage({
  project,
  embed,
  inputValues,
  dataAccess,
}: {
  project: string
  embed: EmbedConfig
  inputValues: Record<string, unknown>
  dataAccess?: DataAccess
}) {
  const parent = useContext(EmbedDepthContext)
  const objName = embed.obj_name
  const [model, setModel] = useState<PageModel | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const pageVersion = useObjectVersion('S', project, objName)

  // Cycle / depth guard — computed before any fetch so we never load a
  // page we'd refuse to render.
  const blocked = parent.chain.includes(objName)
    ? `Embedded page "${objName}" skipped (it would create a loop).`
    : parent.depth >= MAX_EMBED_DEPTH
      ? `Embedded page "${objName}" skipped (nesting limit reached).`
      : null

  useEffect(() => {
    if (blocked || !project || !objName) { setModel(null); return }
    let cancelled = false
    setLoading(true)
    setError(null)
    loadPageObject(project, objName)
      .then((data) => {
        if (cancelled) return
        const root = data?.content as ({ page?: PageModel } & PageModel) | null | undefined
        const m = (root?.page ?? root) as PageModel | undefined
        if (!m) { setError(`Page "${objName}" not yet generated`); setModel(null); return }
        setModel(m)
      })
      .catch(() => { if (!cancelled) setError('Failed to load embedded page') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, objName, pageVersion, blocked])

  // Arg-mapping: this page's input values (or static literals) → the
  // embedded page's PageInput names.
  const extraVars = useMemo(() => {
    const out: Record<string, unknown> = {}
    for (const a of embed.args || []) {
      if (!a.target) continue
      out[a.target] = a.source.source === 'page_input'
        ? (a.source.name ? inputValues[a.source.name] : undefined)
        : a.source.value
    }
    return out
  }, [embed.args, inputValues])

  if (blocked) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{blocked}</span>
      </div>
    )
  }
  if (!objName) {
    return (
      <div style={emptyStyle}>
        <FileText size={20} />
        <span>Pick a smart page in the property panel to embed.</span>
      </div>
    )
  }
  if (loading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading embedded page…</span>
      </div>
    )
  }
  if (error) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{error}</span>
      </div>
    )
  }
  if (!model) return null

  const childState: EmbedDepthState = {
    depth: parent.depth + 1,
    chain: [...parent.chain, objName],
  }
  return (
    <EmbedDepthContext.Provider value={childState}>
      <SmartPageRuntime
        page={model}
        mode="preview"
        docId={null}
        project={project}
        dataAccess={dataAccess}
        extraVars={extraVars}
      />
    </EmbedDepthContext.Provider>
  )
}

function TransactionPreview({
  project,
  embed,
  inputValues,
  dataAccess,
}: {
  project: string
  embed: EmbedConfig
  inputValues: Record<string, unknown>
  dataAccess?: DataAccess
}) {
  const [model, setModel] = useState<TransactionModel | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Bumped by TransactionDesignerPage on save — forces a refetch so
  // edits made elsewhere in the same session reflect here immediately.
  const txnVersion = useObjectVersion('T', project, embed.obj_name)

  useEffect(() => {
    if (!embed.obj_name || !project) {
      setModel(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    embedLoadModel(dataAccess, project, embed.obj_name)
      .then((res) => {
        if (cancelled) return
        const content = res?.content as { transaction?: TransactionModel } | TransactionModel | null
        if (!content) {
          setModel(null)
          setError(`Transaction "${embed.obj_name}" not yet generated`)
          return
        }
        const m = (content as { transaction?: TransactionModel }).transaction || content
        setModel(m as TransactionModel)
      })
      .catch(() => {
        if (cancelled) return
        setError('Failed to load transaction')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [project, embed.obj_name, txnVersion, dataAccess])

  if (!embed.obj_name) {
    return (
      <div style={emptyStyle}>
        <FileText size={20} />
        <span>Pick a transaction in the property panel to embed.</span>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading transaction…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{error}</span>
      </div>
    )
  }

  if (!model) return null

  // Capture mode renders all sql_models stacked: sql_models[0] as a
  // single-record header form + sql_models[1..] as detail grids. The
  // page designer canvas only previews the structure; the actual
  // input wiring + Save button live in the runtime block.
  if (embed.renderMode === 'capture') {
    return <CapturePreview embed={embed} model={model} />
  }

  const formIndex = embed.form_no ? Math.max(0, parseInt(embed.form_no, 10) - 1) : 0
  const form = model.sql_models?.[formIndex]?.sql_model

  if (!form) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>Form #{(formIndex + 1)} not found in {embed.obj_name}</span>
      </div>
    )
  }

  const modeLabel = embed.mode === 'list' ? 'List' : embed.mode === 'add' ? 'Add' : 'Edit'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Embed banner — shows mode + obj_name + active filters */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 10px', fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)', borderRadius: 6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontWeight: 600, padding: '2px 8px', borderRadius: 12,
            background: 'var(--color-accent-weak)', color: 'var(--color-accent)',
          }}>{modeLabel}</span>
          <span>embed of</span>
          <span style={{ fontFamily: 'monospace', color: 'var(--color-text)' }}>{embed.obj_name}</span>
        </div>
        {embed.mode === 'list' && embed.filters.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {embed.filters.map((f) => (
              <span
                key={f.id}
                style={{
                  padding: '2px 8px', borderRadius: 12,
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  fontFamily: 'monospace',
                }}
              >{resolveFilterDisplay(f, inputValues)}</span>
            ))}
          </div>
        )}
      </div>

      {/* Same render as the transaction object editor */}
      {embed.mode === 'list'
        ? <GridPreviewView form={form} />
        : <FormPreviewView form={form} />}
    </div>
  )
}

const emptyStyle: React.CSSProperties = {
  padding: '20px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: 'var(--color-bg)',
  border: '1px dashed var(--color-border)',
  borderRadius: 8,
  color: 'var(--color-text-secondary)',
  fontSize: '0.9125rem',
}

// ─── Dashboard / Visual embed preview ─────────────────────────
// We don't reproduce the full dashboard / chart render in the page
// designer (that pipeline lives in the Dashboard / Visual designers and
// in the runtime). Show a labeled placeholder with metadata pulled from
// the saved JSON so the page author knows what they're embedding.

interface DashOrVisMeta {
  title: string
  subtitle?: string
  detail?: string
}

// Dashboard auto height: derived from its tile grid (same geometry the renderer
// uses — 70px rows + 8px gap + 8px padding/side) so every tile is visible and
// the embed is exactly as tall as its content. (Single visuals size themselves
// via InsightVisual's autoHeight; only dashboards need this computation.)
const DASH_ROW_PX = 70
const DASH_GAP = 8
const DASH_PAD = 8
function dashboardAutoHeight(maxRow: number): number {
  if (!Number.isFinite(maxRow) || maxRow <= 0) return 480
  return dashGridHeightPx(maxRow, DASH_ROW_PX, DASH_GAP, DASH_PAD)
}

// Largest (Y + HEIGHT) across a dashboard's tile POSITIONs → the grid row count.
function dashboardMaxRow(visuals: unknown[]): number {
  let maxRow = 0
  for (const v of visuals) {
    const pos = (v as { POSITION?: { Y?: number; HEIGHT?: number } })?.POSITION
    if (!pos) continue
    const bottom = (Number(pos.Y) || 0) + Math.max(1, Number(pos.HEIGHT) || 4)
    if (bottom > maxRow) maxRow = bottom
  }
  return maxRow
}

function DashboardOrVisualPreview({
  project,
  embed,
  inputValues,
  onSetInput,
  bodyHeight,
}: {
  project: string
  embed: EmbedConfig
  inputValues: Record<string, unknown>
  onSetInput?: (name: string, value: unknown) => void
  bodyHeight?: string
}) {
  const [meta, setMeta] = useState<DashOrVisMeta | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isDashboard = embed.obj_type === 'D'
  // Map this page's variables / static literals onto the visual's prompt
  // parameters (or the dashboard's global criteria). Recomputed when the
  // mappings or the underlying page-variable values change.
  const params = useMemo(
    () => resolveVisualArgs(embed.args, inputValues),
    [embed.args, inputValues],
  )

  useEffect(() => {
    if (!project || !embed.obj_name) {
      setMeta(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    const loader = isDashboard
      ? loadDashboardObject(project, embed.obj_name)
      : loadVisualObject(project, embed.obj_name)
    loader
      .then((res) => {
        if (cancelled) return
        const content = res?.content as Record<string, unknown> | null | undefined
        if (!content) {
          setMeta(null)
          setError(`${isDashboard ? 'Dashboard' : 'Visual'} "${embed.obj_name}" not yet generated`)
          return
        }
        if (isDashboard) {
          const d = (content.DASHBOARD as Record<string, unknown>) || {}
          const visuals = (d.VISUALS as unknown[]) || []
          setMeta({
            title: (d.DASHBOARD_NAME as string) || embed.obj_name,
            subtitle: (d.DASHBOARD_DESCR as string) || '',
            detail: `${visuals.length} visual${visuals.length === 1 ? '' : 's'}`,
          })
        } else {
          const sqlmodel = (content.SQLMODEL as Record<string, unknown>) || {}
          const layout = ((content.LAYOUT_DATA as Record<string, unknown>)?.visualLayout as Record<string, unknown>) || {}
          const visualType =
            (sqlmodel.VISUAL_NAME as string) || (layout.VisualName as string) || 'Visual'
          setMeta({
            title: (content.obj_name as string) || embed.obj_name,
            subtitle: (sqlmodel.SCHEMA_NAME as string) || '',
            detail: visualType,
          })
        }
      })
      .catch(() => {
        if (!cancelled) setError(`Failed to load ${isDashboard ? 'dashboard' : 'visual'}`)
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [project, embed.obj_name, isDashboard])

  if (!embed.obj_name) {
    return (
      <div style={emptyStyle}>
        {isDashboard ? <LayoutDashboard size={20} /> : <BarChart3 size={20} />}
        <span>Pick a {isDashboard ? 'dashboard' : 'visual'} in the property panel to embed.</span>
      </div>
    )
  }
  if (loading) {
    return (
      <div style={emptyStyle}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading {isDashboard ? 'dashboard' : 'visual'}…</span>
      </div>
    )
  }
  if (error) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>{error}</span>
      </div>
    )
  }
  if (!meta) return null

  // The badge/name/type header is intentionally omitted — the chart speaks for
  // itself and the property panel already shows what's embedded. The body sizes
  // to its content (a card stays short, a chart gets a default height, a
  // dashboard fits its tile grid) rather than filling a fixed block height.
  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: 8,
      overflow: 'hidden',
      background: 'var(--color-bg)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ position: 'relative' }}>
        <EmbedLiveBody
          project={project}
          objName={embed.obj_name}
          isDashboard={isDashboard}
          params={params}
          onVisualSelect={makeVisualSelectHandler(embed, onSetInput)}
          fixedHeight={bodyHeight}
        />
      </div>
    </div>
  )
}

// ─── Report (obj_type 'R') embed ──────────────────────────────────
// The shared ReportViewer over the platform /api/report-data endpoints, with
// the embed's args resolved onto the report's prompt params (page-var /
// static binding — visual parity). The viewer owns criteria + auto-run +
// export; the block height bounds it (reports are documents, so a default
// height with the iframe scrolling inside is the natural shape).
const REPORT_EMBED_DEFAULT_HEIGHT = '480px'

function ReportEmbedPreview({
  project, embed, inputValues, bodyHeight,
}: {
  project: string
  embed: EmbedConfig
  inputValues: Record<string, unknown>
  bodyHeight?: string
}) {
  const params = useMemo(
    () => resolveVisualArgs(embed.args, inputValues),
    [embed.args, inputValues],
  )
  if (!embed.obj_name) {
    return (
      <div style={emptyStyle}>
        <FileText size={20} />
        <span>Pick a report in the property panel to embed.</span>
      </div>
    )
  }
  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: 8,
      overflow: 'hidden',
      background: 'var(--color-bg)',
      height: bodyHeight ?? REPORT_EMBED_DEFAULT_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
    }}>
      <ReportEmbedBody project={project} objName={embed.obj_name} params={params} />
    </div>
  )
}

// Renders the embedded visual/dashboard with live data from the project's
// preview DB, using the shared <InsightVisual> engine — the same renderer the
// visual/dashboard designer previews use. Replaces the old "renders at runtime"
// placeholder so authors see the real chart inside the page.
// A model-backed tile carries no server dataset (its query was never run) — the
// engine renders from `model`/`plan` instead, so this only satisfies the prop.
const EMBED_EMPTY_JSON_DATA = [{ id: 0 }] as unknown as NonNullable<
  DashboardDataResult['tiles'][number]['jsonData']
>

interface EmbedDrillThrough {
  SOURCE_VISUAL_ID: string
  // The source tile's column this link answers to ('' / absent = any column).
  SOURCE_COLUMN?: string
  // 'dashboard' (default when absent/unknown) | 'visual' | 'self'
  TARGET_TYPE?: string
  TARGET_DASHBOARD_ID?: string
  TARGET_VISUAL_ID?: string
  PARAM_MAPPING: Record<string, string>
}
interface EmbedDrillLevel {
  // What the level renders: another dashboard's tile grid, or ONE visual
  // full-size. ('self' drills never push a level.)
  target: 'dashboard' | 'visual'
  objName: string
  globals: Record<string, string>
}

function embedLookupField(row: Record<string, unknown>, name: string): unknown {
  if (name in row) return row[name]
  const lk = name.toLowerCase()
  const hit = Object.keys(row).find((k) => k.toLowerCase() === lk)
  return hit ? row[hit] : undefined
}

function EmbedLiveBody({
  project, objName, isDashboard, params, onVisualSelect, fixedHeight,
}: { project: string; objName: string; isDashboard: boolean; params: Record<string, unknown>; onVisualSelect?: (row: Record<string, unknown>) => void; fixedHeight?: string }) {
  const [visual, setVisual] = useState<VisualDataPayload | null>(null)
  const [dash, setDash] = useState<DashboardDataResult | null>(null)
  // Dataset of a drilled-into SINGLE visual (TARGET_TYPE 'visual' levels).
  const [drillVisual, setDrillVisual] = useState<VisualDataPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // Underlying-data target: the client-side rows behind a double-clicked point
  // (or the ⋮ menu), or null when the modal is closed.
  const [underlying, setUnderlying] = useState<UnderlyingPayload | null>(null)
  // Drill-through stack (dashboards only). Empty → the base dashboard.
  const [drill, setDrill] = useState<EmbedDrillLevel[]>([])
  const active = drill.length ? drill[drill.length - 1] : null
  // Cross-filter ('self' drill) values applied to the BASE dashboard's global
  // criteria from a clicked row. Shown as removable chips (this surface has no
  // dashboard criteria bar to reflect them in).
  const [selfFilters, setSelfFilters] = useState<Record<string, string>>({})
  // One click answering to several drill-throughs opens a short chooser here.
  const [drillChoice, setDrillChoice] = useState<{
    at: { x: number; y: number }; choices: EmbedDrillThrough[]; row: Record<string, unknown>; info?: SelectInfo
  } | null>(null)
  const { pointer: drillPointer, onPointerDownCapture: onDrillPointerDown } = useLastPointer()
  // ── Quick filter mode (dashboards) ─────────────────────────────────────
  // Distinct from the 'self' drill cross-filter above: that one re-queries via
  // the dashboard's authored GLOBAL_CRITERIA, this one is ad-hoc and filters the
  // rows each tile has ALREADY loaded (one shared filter expression evaluated by
  // each <InsightVisual>). Off until the user enters the mode.
  const [quickMode, setQuickMode] = useState(false)
  const [chips, setChips] = useState<QuickFilterChip[]>([])
  const [tileFilterResults, setTileFilterResults] = useState<Record<string, FilterResult>>({})
  const chipsKey = quickFilterKey(chips)
  const quickFilterExpr = useMemo(
    () => chipsToFilterExpression(chips),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chipsKey],
  )
  const applyQuickPicks = (
    picks: QuickFilterPick[], additive: boolean, sourceVisual?: string,
  ) => {
    if (!picks.length) return
    setChips((current) => mergeQuickFilterPicks(current, picks, { additive, sourceVisual }))
  }
  // User-entered criteria (visuals): `critDraft` is what the user is typing in
  // the criteria form; `critApplied` is what's actually merged into the fetch
  // (set on "Apply"). Bound page-var `params` still win as the base layer.
  const [critDraft, setCritDraft] = useState<Record<string, string>>({})
  const [critApplied, setCritApplied] = useState<Record<string, string>>({})
  const effectiveParams = useMemo(
    () => ({ ...params, ...critApplied }), [params, critApplied],
  )
  // Single-visual SINGLE click: run the author's selection-var handler (writes
  // the embed's selectedIdVar / selectedDataVar page variables). Underlying data
  // is a separate DOUBLE-click / ⋮-menu action so the two never compete.
  const handleVisualSelect = (row: Record<string, unknown>) => {
    onVisualSelect?.(row)
  }
  // Double-click a point (or the ⋮ "View underlying data") → row-level detail,
  // computed client-side by the engine (no backend round-trip).
  const handleVisualUnderlying = (payload: UnderlyingPayload) => setUnderlying(payload)
  // Base-dashboard globals: bound page-var args + any applied cross-filters.
  const dashGlobals = useMemo(
    () => ({ ...params, ...selfFilters }), [params, selfFilters],
  )
  // Stable dependency for the param maps so the fetch re-runs when a bound
  // page variable (or an applied criterion / cross-filter) changes value.
  const paramsKey = JSON.stringify(effectiveParams)
  const dashKey = JSON.stringify(dashGlobals)

  // Transport for criteria F2 lookups + on-change item_change (saved visual).
  const criteriaRuntime = useMemo(
    () => makeVisualCriteriaRuntime({
      project, kind: 'visual', obj_name: objName,
      notify: { success: toast.success, error: toast.error, info: (m) => toast(m) },
    }),
    [project, objName],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true); setErr(null); setVisual(null); setDash(null); setDrillVisual(null)
    const p = isDashboard
      ? active?.target === 'visual'
        // Drilled into a SINGLE visual: fetch its dataset with the mapped
        // params from the clicked row.
        ? fetchVisualData({ project, obj_name: active.objName, params: active.globals })
            .then((d) => { if (!cancelled) setDrillVisual(d) })
        : fetchDashboardData(
            active
              ? { project, obj_name: active.objName, global_criteria: active.globals, in_memory: true }
              : { project, obj_name: objName, global_criteria: dashGlobals, in_memory: true },
          ).then((d) => { if (!cancelled) setDash(d) })
      : fetchVisualData({ project, obj_name: objName, params: effectiveParams })
          .then((d) => {
            if (cancelled) return
            setVisual(d)
            // Seed the criteria form with each prompt's applied/resolved value.
            if (d.promptVars?.length) {
              setCritDraft((prev) => {
                const next = { ...prev }
                let changed = false
                for (const pv of d.promptVars!) {
                  if (next[pv.name] != null && next[pv.name] !== '') continue
                  const raw = d.resolvedParams?.[pv.name] ?? pv.default
                  const seed = raw != null && !Array.isArray(raw) ? String(raw) : ''
                  if (seed !== '') { next[pv.name] = seed; changed = true }
                }
                return changed ? next : prev
              })
            }
          })
    p.catch((e) => {
      if (cancelled) return
      // readDataError understands the structured `{code, message}` detail an
      // unreachable data source answers with, as well as a plain string.
      setErr(readDataError(e).message || 'Failed to load data')
    }).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, objName, isDashboard, active, paramsKey, dashKey])

  // Auto-refresh: a visual carries its interval in the autoRefreshSeconds
  // option; a dashboard in its REFRESH_INTERVAL_SECONDS. Re-fetch silently —
  // the current chart stays on screen and a failed poll keeps the last data.
  const refreshSeconds = isDashboard
    ? Number(dash?.dashboard?.REFRESH_INTERVAL_SECONDS) || 0
    : (visual && !visual.needsInput
        ? readAutoRefreshSeconds(visual.optionsArr, visual.visualLayout)
        : 0)
  useAutoRefresh(refreshSeconds, async () => {
    if (isDashboard) {
      if (active?.target === 'visual') {
        const d = await fetchVisualData({ project, obj_name: active.objName, params: active.globals })
        if (!d.needsInput) setDrillVisual(d)
        return
      }
      const d = await fetchDashboardData(
        active
          ? { project, obj_name: active.objName, global_criteria: active.globals, in_memory: true }
          : { project, obj_name: objName, global_criteria: dashGlobals, in_memory: true },
      )
      setDash(d)
    } else {
      const d = await fetchVisualData({ project, obj_name: objName, params: effectiveParams })
      if (!d.needsInput) setVisual(d)
    }
  })

  // Reset the drill stack + criteria + cross-filters if the embedded object
  // changes.
  useEffect(() => {
    setDrill([]); setCritDraft({}); setCritApplied({}); setSelfFilters({})
    setQuickMode(false); setChips([]); setTileFilterResults({})
  }, [project, objName])

  // One in-memory model per data source: supported tiles were NOT queried — they
  // fold their dataset out of these, so a filter recomputes locally.
  const { models, loading: modelsLoading } = useVisualModels(dash?.models)

  // What a tile did with the chips: a visual reports itself, a report tile and a
  // failed tile are reported here (they never evaluate the expression).
  const quickStatusFor = (
    t: DashboardDataResult['tiles'][number],
  ): TileQuickFilterStatus | undefined => {
    if (!chips.length || drill.length > 0) return undefined
    const allSkipped = (reason: string) => ({
      applied: [],
      skipped: chips.map((c) => ({ column: c.column, reason })),
    })
    if (String(t.TILE_KIND || '').toLowerCase() === 'report') return allSkipped(REASON_REPORT_TILE)
    if (t.error) return allSkipped(REASON_TILE_ERROR)
    return tileFilterResults[t.VISUAL_ID]
  }

  const drillThrough = (dash?.dashboard.DRILL_THROUGH ?? []) as EmbedDrillThrough[]
  // Target dashboards' titles for the chooser ("Open Partner sales").
  const titleLoader = useMemo(() => dashboardTitleLoader(project), [project])
  const targetTitle = useDrillTargetTitles(project, drillThrough, titleLoader)
  // A tile may carry several links, one per column (resolveDrillThrough).
  const followDrill = (dt: EmbedDrillThrough, row: Record<string, unknown>, info?: SelectInfo) => {
    const targetType = drillTargetType(dt)
    const mapped = drillMappedValues(dt, row, info)
    if (targetType === 'self') {
      // Cross-filter the CURRENT dashboard — no new level. On the base level
      // the values show as removable chips; on a drilled level they merge
      // into that level's globals.
      if (Object.keys(mapped).length === 0) return
      if (drill.length) {
        setDrill((s) => s.map((l, i) => (
          i === s.length - 1 ? { ...l, globals: { ...l.globals, ...mapped } } : l
        )))
      } else {
        setSelfFilters((f) => ({ ...f, ...mapped }))
      }
      return
    }
    if (targetType === 'visual') {
      if (!dt.TARGET_VISUAL_ID) {
        console.warn('Drill-through entry has TARGET_TYPE visual but no TARGET_VISUAL_ID', dt)
        return
      }
      setDrill((s) => [...s, { target: 'visual', objName: dt.TARGET_VISUAL_ID!, globals: mapped }])
      return
    }
    if (!dt.TARGET_DASHBOARD_ID) return
    setDrill((s) => [...s, { target: 'dashboard', objName: dt.TARGET_DASHBOARD_ID!, globals: mapped }])
  }
  const onTileSelect = (visualId: string, row: Record<string, unknown>, info?: SelectInfo) => {
    // No configured drill-through for this click → nothing (underlying data is
    // a double-click / ⋮-menu action).
    const choices = resolveDrillThrough(drillThrough, visualId, info)
    if (choices.length === 0) return
    if (choices.length === 1) { followDrill(choices[0], row, info); return }
    setDrillChoice({ at: { ...drillPointer.current }, choices, row, info })
  }
  const drillChooser = drillChoice && (
    <DrillChooser
      at={drillChoice.at}
      choices={drillChoice.choices}
      names={targetTitle}
      onClose={() => setDrillChoice(null)}
      onPick={(dt) => { const { row, info } = drillChoice; setDrillChoice(null); followDrill(dt, row, info) }}
    />
  )

  // Absolute-fill overlay — only used INSIDE a dashboard tile (a fixed-size grid
  // cell) for its error / no-data message.
  const fill: CSSProperties = {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 8, padding: 16, textAlign: 'center', fontSize: 13,
  }
  // Flow status box — the body sizes to content now, so top-level loading /
  // error / empty states need their own (modest) height instead of filling.
  const statusBox: CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: 16, textAlign: 'center', fontSize: 13, minHeight: 120,
  }
  // Shared underlying-data modal — included in the interactive render branches.
  // Rows are computed client-side by the engine (no backend round-trip).
  const underlyingModal = underlying && (
    <UnderlyingDataModal
      open
      jsonData={underlying.jsonData}
      visualLayout={underlying.visualLayout}
      filters={underlying.filters}
      onClose={() => setUnderlying(null)}
    />
  )

  if (loading) {
    return <div style={{ ...statusBox, color: 'var(--color-text-secondary)' }}><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading data…</div>
  }
  if (err) {
    return <div style={{ ...statusBox, color: 'var(--color-text-secondary)' }}><AlertCircle size={16} /> {err}</div>
  }
  if (!isDashboard && visual) {
    if (visual.needsInput) {
      // Render a fillable criteria form (with F2 lookup + item_change) so the
      // user can supply the missing prompts and run the visual here.
      return (
        <div style={{ padding: 14, display: 'grid', gap: 12 }}>
          <VisualCriteriaForm
            project={project}
            promptVars={(visual.promptVars ?? []) as unknown as VisualCriteriaPromptVar[]}
            values={critDraft}
            onChange={(name, val) => setCritDraft((v) => ({ ...v, [name]: val }))}
            onSubmit={() => setCritApplied(critDraft)}
            runtime={criteriaRuntime}
          />
          <div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setCritApplied(critDraft)}
            >
              Apply
            </button>
            {visual.missingParams?.length ? (
              <span style={{ marginLeft: 10, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                Required: {visual.missingParams.join(', ')}
              </span>
            ) : null}
          </div>
        </div>
      )
    }
    // A DOCUMENT visual's output is a laid-out document, not a chart. The
    // embed hands it to the shared preview, which runs it and lays out the
    // documents it produced — the same component the designer preview and the
    // Run view mount, so an embedded mail merge is the one they reviewed.
    if (isDocumentVisual(visual.visualLayout?.className,
                         visual.visualLayout?.ID)) {
      return (
        <div style={fixedHeight
          ? { height: fixedHeight, display: 'flex', flexDirection: 'column' }
          : { minHeight: 320, display: 'flex', flexDirection: 'column' }}>
          {/* The criteria the page's bound arguments already resolved to —
              without them an embedded document ran UNFILTERED here while the
              downloaded runtime ran it filtered, which is the same embed
              showing two different sets of documents. */}
          <VisualDocumentPreview
            project={project} objName={objName}
            params={visual.resolvedParams || undefined}
          />
        </div>
      )
    }
    if ((visual.jsonData?.length ?? 0) <= 1) {
      return <div style={{ ...statusBox, color: 'var(--color-text-secondary)' }}>No rows.</div>
    }
    // Honor an explicit block height: fill it. Otherwise content height —
    // native visuals (cards/table/feeds…) take their natural height; charts
    // fall back to a sensible default — never a fixed fill.
    if (fixedHeight) {
      return (
        <>
          <div style={{ position: 'relative', height: fixedHeight }}>
            <InsightVisual
              jsonData={visual.jsonData}
              visualLayout={visual.visualLayout}
              optionsArr={visual.optionsArr}
              onSelect={handleVisualSelect}
              onShowUnderlying={handleVisualUnderlying}
              truncated={visual.truncated}
              rowLimit={visual.rowLimit}
              rowCeiling={visual.rowCeiling}
              rowLimitAuthored={visual.rowLimitAuthored}
              rowLimitFixed={visual.rowLimitFixed}
              menuHeader
              style={{ position: 'absolute', inset: 0, height: '100%' }}
            />
          </div>
          {underlyingModal}
        </>
      )
    }
    return (
      <>
        <InsightVisual
          jsonData={visual.jsonData}
          visualLayout={visual.visualLayout}
          optionsArr={visual.optionsArr}
          onSelect={handleVisualSelect}
          onShowUnderlying={handleVisualUnderlying}
          truncated={visual.truncated}
          rowLimit={visual.rowLimit}
          rowCeiling={visual.rowCeiling}
          rowLimitAuthored={visual.rowLimitAuthored}
          rowLimitFixed={visual.rowLimitFixed}
          autoHeight
          menuHeader
          style={{ width: '100%' }}
        />
        {underlyingModal}
      </>
    )
  }
  // Drilled into a SINGLE visual (TARGET_TYPE 'visual'): render it full-size
  // with the same Back affordance the dashboard levels use.
  if (isDashboard && active?.target === 'visual') {
    if (!drillVisual) return null
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', fontSize: 13, color: 'var(--color-text-secondary)', flexShrink: 0 }}>
          <button onClick={() => setDrill((s) => s.slice(0, -1))} style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer', padding: '1px 6px', color: 'var(--color-text)' }}>← Back</button>
          <span>Drilled into <span style={{ fontFamily: 'monospace' }}>{active.objName}</span></span>
        </div>
        {drillVisual.needsInput ? (
          <div style={{ ...statusBox, color: 'var(--color-text-secondary)' }}>
            Needs input: {(drillVisual.missingParams || []).join(', ') || 'required parameters missing'}.
          </div>
        ) : (drillVisual.jsonData?.length ?? 0) <= 1 ? (
          <div style={{ ...statusBox, color: 'var(--color-text-secondary)' }}>No rows.</div>
        ) : fixedHeight ? (
          <div style={{ position: 'relative', height: fixedHeight }}>
            <InsightVisual
              jsonData={drillVisual.jsonData}
              visualLayout={drillVisual.visualLayout}
              optionsArr={drillVisual.optionsArr}
              truncated={drillVisual.truncated}
              rowLimit={drillVisual.rowLimit}
              rowCeiling={drillVisual.rowCeiling}
              rowLimitAuthored={drillVisual.rowLimitAuthored}
              rowLimitFixed={drillVisual.rowLimitFixed}
              menuHeader
              style={{ position: 'absolute', inset: 0, height: '100%' }}
            />
          </div>
        ) : (
          <InsightVisual
            jsonData={drillVisual.jsonData}
            visualLayout={drillVisual.visualLayout}
            optionsArr={drillVisual.optionsArr}
            truncated={drillVisual.truncated}
            rowLimit={drillVisual.rowLimit}
            rowCeiling={drillVisual.rowCeiling}
            rowLimitAuthored={drillVisual.rowLimitAuthored}
            rowLimitFixed={drillVisual.rowLimitFixed}
            autoHeight
            menuHeader
            style={{ width: '100%' }}
          />
        )}
      </div>
    )
  }
  if (isDashboard && dash) {
    // Honor an explicit block height; otherwise size the tile grid to its rows
    // (capped at one screen) so the embed is exactly as tall as its tiles.
    const gridHeight: string | number = fixedHeight ?? dashboardAutoHeight(dashboardMaxRow(dash.tiles))
    return (
      <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {drill.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', fontSize: 13, color: 'var(--color-text-secondary)', flexShrink: 0 }}>
            <button onClick={() => setDrill((s) => s.slice(0, -1))} style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer', padding: '1px 6px', color: 'var(--color-text)' }}>← Back</button>
            <span>Drilled into <span style={{ fontFamily: 'monospace' }}>{active?.objName}</span></span>
          </div>
        )}
        {/* Cross-filter ('self' drill) chips — visible + clearable, since this
            surface has no dashboard criteria bar reflecting the values. */}
        {drill.length === 0 && Object.keys(selfFilters).length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, padding: '4px 8px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)', fontSize: 13, color: 'var(--color-text-secondary)', flexShrink: 0 }}>
            <span>Filtered:</span>
            {Object.entries(selfFilters).map(([k, v]) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 6px', border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-bg)', color: 'var(--color-text)' }}>
                {k} = {v}
                <button
                  onClick={() => setSelfFilters((f) => {
                    const next = { ...f }
                    delete next[k]
                    return next
                  })}
                  title="Clear filter"
                  aria-label={`Clear filter ${k}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-text-secondary)', fontSize: 13, lineHeight: 1 }}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
        {/* Quick filter mode: the switch, then the applied chips. Compact chrome
            — an embed has no criteria bar to dock the toggle into. */}
        {drill.length === 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)', flexShrink: 0,
          }}>
            <QuickFilterToggle
              compact
              active={quickMode}
              count={chips.length}
              onToggle={() => setQuickMode((m) => !m)}
            />
          </div>
        )}
        {drill.length === 0 && (
          <QuickFilterBar
            compact
            active={quickMode}
            chips={chips}
            reach={quickFilterReach(dash.tiles.map((t) => quickStatusFor(t)))}
            onRemove={(column, value) => setChips((c) => removeQuickFilterValue(c, column, value))}
            onClearAll={() => setChips([])}
            onExit={() => setQuickMode(false)}
          />
        )}
        <div onPointerDownCapture={onDrillPointerDown} style={{ height: gridHeight, maxHeight: fixedHeight ? undefined : '100vh', overflow: 'auto', ...dashGridContainerStyle(DASH_ROW_PX, DASH_GAP, DASH_PAD) }}>
          {dash.tiles.map((t) => {
            // Only the base dashboard is quick-filterable — a drilled level is a
            // different dashboard/visual with its own data.
            const qfOn = quickMode && drill.length === 0
            const indication = tileQuickFilterIndication(quickStatusFor(t), chips)
            const liveReason = t.plan && !t.plan.supported
              ? (MODEL_FALLBACK_REASON_TEXT[t.plan.reason || ''] || 'it cannot be served from the shared dataset')
              : null
            return (
              <div
                key={t.VISUAL_ID}
                style={{
                  ...dashTileStyle(t.POSITION, DASH_GAP),
                  border: indication && indication.state === 'none'
                    ? '1px solid var(--pg-warning, #d97706)'
                    : qfOn
                      ? '1px dashed var(--pg-primary, #3b82f6)'
                      : '1px solid var(--color-border)',
                  borderRadius: 8, overflow: 'hidden', position: 'relative', minWidth: 0,
                }}
              >
                {/* Whether the quick filter reached this tile, and whether it was
                    queried live — the shared badge, identical on every surface. */}
                {(indication || liveReason) && (
                  <span style={{ position: 'absolute', top: 4, left: 6, zIndex: 3 }}>
                    <TileFilterBadge indication={indication} liveReason={liveReason} compact />
                  </span>
                )}
                {t.error ? (
                  <div style={{ ...fill, color: 'var(--color-text-secondary)' }}>{t.error}</div>
                ) : t.TILE_KIND === 'report' && t.REPORT_OBJ ? (
                  // Report tile — the shared ReportViewer fills the tile with
                  // the server-resolved bound params (criteria bindings).
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                    <ReportEmbedBody project={project} objName={t.REPORT_OBJ} params={t.reportParams} />
                  </div>
                ) : t.modelBacked && !models.get(t.plan?.sourceKey || '') ? (
                  <div style={{ ...fill, color: 'var(--color-text-secondary)' }}>
                    {modelsLoading ? 'Preparing shared dataset…' : 'Shared dataset unavailable.'}
                  </div>
                ) : (t.jsonData || t.modelBacked) && t.visualLayout ? (
                  <InsightVisual
                    jsonData={t.jsonData ?? EMBED_EMPTY_JSON_DATA}
                    truncated={t.truncated}
                    rowLimit={t.rowLimit}
                    rowCeiling={t.rowCeiling}
                    rowLimitAuthored={t.rowLimitAuthored}
                    rowLimitFixed={t.rowLimitFixed}
                    model={t.plan?.sourceKey ? models.get(t.plan.sourceKey) : undefined}
                    plan={t.plan}
                    visualLayout={t.visualLayout}
                    optionsArr={t.optionsArr}
                    onSelect={(row, info) => onTileSelect(t.VISUAL_ID, row, info)}
                    onShowUnderlying={(payload) => setUnderlying(payload)}
                    quickFilterMode={qfOn}
                    onQuickFilter={(picks, opts) => applyQuickPicks(picks, opts.additive, t.VISUAL_NAME || t.VISUAL_ID)}
                    filter={drill.length === 0 ? quickFilterExpr : null}
                    onFilterResult={(result) => setTileFilterResults((prev) => ({ ...prev, [t.VISUAL_ID]: result }))}
                    menuHeader
                    style={{ position: 'absolute', inset: 0, height: '100%' }}
                  />
                ) : (
                  <div style={{ ...fill, color: 'var(--color-text-secondary)' }}>No data.</div>
                )}
              </div>
            )
          })}
          {dash.tiles.length === 0 && <div style={{ ...fill, color: 'var(--color-text-secondary)', gridColumn: '1 / -1' }}>No tiles.</div>}
        </div>
      </div>
      {underlyingModal}
      {drillChooser}
      </>
    )
  }
  return null
}

// ─── Capture-mode embed preview ───────────────────────────────────
// Dispatches on embed.captureTemplate to render each layout the
// runtime supports:
//   single-classic  — header form stacked above detail grids
//   multi-tabular   — flat header grid w/ detail tables under each row
//   multi-tab       — tab strip; active tab shows form + details
//   multi-accordion — vertical accordion of full forms
// Read-only structural preview; runtime renders real inputs + Save.

function CapturePreview({
  embed,
  model,
}: {
  embed: EmbedConfig
  model: TransactionModel
}) {
  const sqlModels = model.sql_models || []
  if (sqlModels.length === 0) {
    return (
      <div style={{ ...emptyStyle, color: 'var(--color-text-secondary)' }}>
        <AlertCircle size={18} />
        <span>No sql_models in {embed.obj_name}</span>
      </div>
    )
  }
  const header = sqlModels[0]?.sql_model
  const details = sqlModels.slice(1).map((sm) => sm.sql_model).filter(Boolean)
  const saveLabel = embed.captureSaveLabel || 'Save'
  const template: EmbedCaptureTemplate = embed.captureTemplate || 'single-classic'

  const banner = (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '4px 10px', fontSize: '0.8125rem',
      color: 'var(--color-text-secondary)',
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)', borderRadius: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          fontWeight: 600, padding: '2px 8px', borderRadius: 12,
          background: 'rgba(245,158,11,0.18)', color: '#b45309',
        }}>Capture · {templateLabel(template)}</span>
        <span style={{ fontFamily: 'monospace', color: 'var(--color-text)' }}>{embed.obj_name}</span>
      </div>
      <span style={{ fontStyle: 'italic' }}>
        {sqlModels.length === 1
          ? '1 form'
          : `1 header · ${details.length} detail${details.length === 1 ? '' : 's'}`}
      </span>
    </div>
  )

  const saveBar = (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <button
        type="button"
        disabled
        style={{
          padding: '8px 18px', fontSize: '0.9125rem', fontWeight: 600,
          border: 'none', borderRadius: 6,
          background: 'var(--pg-primary, #0f766e)',
          color: 'var(--pg-on-primary, #fff)',
          cursor: 'not-allowed', opacity: 0.85,
        }}
        title="Renders as a working button at runtime; posts to processTransactionRequest."
      >
        {saveLabel}
      </button>
    </div>
  )

  if (template === 'multi-tabular') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {banner}
        {header && (
          <MultiTabularPreview header={header} details={details} />
        )}
        {saveBar}
      </div>
    )
  }

  if (template === 'multi-tab') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {banner}
        {header && (
          <MultiTabPreview header={header} details={details} />
        )}
        {saveBar}
      </div>
    )
  }

  if (template === 'multi-accordion') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {banner}
        {header && (
          <MultiAccordionPreview header={header} details={details} />
        )}
        {saveBar}
      </div>
    )
  }

  // single-classic (default): header form stacked above detail grids
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {banner}
      {header && (
        <div style={captureSectionStyle}>
          <div style={captureSectionHeaderStyle}>
            <span style={{ fontWeight: 600 }}>
              {header.form_title || 'Header'}
            </span>
            <span style={captureBadgeStyle}>Header form</span>
          </div>
          <FormPreviewView form={header} />
        </div>
      )}
      {details.map((d, i) => (
        <div key={i} style={captureSectionStyle}>
          <div style={captureSectionHeaderStyle}>
            <span style={{ fontWeight: 600 }}>
              {d.form_title || `Detail ${i + 1}`}
            </span>
            <span style={captureBadgeStyle}>Detail grid</span>
          </div>
          <GridPreviewView form={d} />
        </div>
      ))}
      {saveBar}
    </div>
  )
}

function templateLabel(t: EmbedCaptureTemplate): string {
  if (t === 'multi-tabular') return 'Tabular'
  if (t === 'multi-tab') return 'Tabbed'
  if (t === 'multi-accordion') return 'Accordion'
  if (t === 'multi-compact') return 'Compact'
  return 'Single'
}

// Visible header columns for the tabular flat grid. Sorted by `x`
// when at least one column carries a layout coord, so the preview
// matches the order set in the transaction editor.
const PREVIEW_DEFAULT_COL_WIDTH = 160

function visibleHeaderCols(form: SqlModel): ColumnDef[] {
  const cols = (form.columns || [])
    .map((c) => c.column)
    .filter((c) => {
      const h = c?.hidden
      return !(h === true || h === 'true')
    })
  const hasX = cols.some((c) => (Number(c.x) || 0) > 0)
  if (!hasX) return cols
  return [...cols].sort((a, b) => (Number(a.x) || 0) - (Number(b.x) || 0))
}

function previewColWidth(col: ColumnDef): number {
  const w = Number(col.width)
  return Number.isFinite(w) && w > 0 ? w : PREVIEW_DEFAULT_COL_WIDTH
}

// ── Multi-tabular preview: flat header grid + collapsible detail tables ──

function MultiTabularPreview({
  header,
  details,
}: {
  header: SqlModel
  details: SqlModel[]
}) {
  const cols = visibleHeaderCols(header)
  const [expanded, setExpanded] = useState<number | null>(details.length > 0 ? 0 : null)
  const sampleRows = 2

  return (
    <div style={captureSectionStyle}>
      <div style={captureSectionHeaderStyle}>
        <span style={{ fontWeight: 600 }}>{header.form_title || 'Records'}</span>
        <span style={captureBadgeStyle}>Tabular grid</span>
      </div>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table style={previewTableStyle}>
          <colgroup>
            {details.length > 0 && <col style={{ width: 28 }} />}
            <col style={{ width: 28 }} />
            <col style={{ width: 16 }} />
            {cols.map((c) => {
              const w = previewColWidth(c)
              return (
                <col
                  key={c.col_id || c.name}
                  width={w}
                  style={{ width: w, minWidth: w, maxWidth: w }}
                />
              )
            })}
          </colgroup>
          <thead>
            <tr>
              {details.length > 0 && <th style={{ ...previewThStyle, width: 28 }}></th>}
              <th style={{ ...previewThStyle, width: 28 }}>#</th>
              <th style={{ ...previewThStyle, width: 16 }}></th>
              {cols.map((c) => {
                const w = previewColWidth(c)
                return (
                  <th
                    key={c.col_id || c.name}
                    style={{
                      ...previewThStyle,
                      width: w, minWidth: w, maxWidth: w,
                      boxSizing: 'border-box',
                    }}
                  >
                    {formatColumnLabel(c)}
                    {isMandatory(c) && <span style={{ color: '#b00020', marginLeft: 2 }}>*</span>}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: sampleRows }).map((_, i) => (
              <PreviewTabularRow
                key={i}
                index={i}
                cols={cols}
                details={details}
                isExpanded={expanded === i}
                onToggle={() => setExpanded(expanded === i ? null : i)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div style={{
        padding: '6px 8px', fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)', fontStyle: 'italic',
      }}>
        + Add record (runtime)
      </div>
    </div>
  )
}

function PreviewTabularRow({
  index,
  cols,
  details,
  isExpanded,
  onToggle,
}: {
  index: number
  cols: ColumnDef[]
  details: SqlModel[]
  isExpanded: boolean
  onToggle: () => void
}) {
  const status = index === 0 ? 'unsaved' : 'saved'
  const dotColor = status === 'unsaved' ? '#f59e0b' : '#16a34a'
  return (
    <>
      <tr>
        {details.length > 0 && (
          <td style={previewTdStyle}>
            <button
              type="button"
              onClick={onToggle}
              title={isExpanded ? 'Collapse details' : 'Expand details'}
              style={{
                background: 'transparent', border: 'none',
                cursor: 'pointer', color: 'var(--color-text-secondary)',
                padding: 0, fontSize: '0.8425rem',
              }}
            >{isExpanded ? '▾' : '▸'}</button>
          </td>
        )}
        <td style={{ ...previewTdStyle, color: 'var(--color-text-secondary)' }}>{index + 1}</td>
        <td style={previewTdStyle}>
          <span style={{
            display: 'inline-block', width: 8, height: 8,
            borderRadius: '50%', background: dotColor,
          }} title={status} />
        </td>
        {cols.map((c) => (
          <td key={c.col_id || c.name} style={previewTdStyle}>
            <span style={previewCellStyle}>{previewPlaceholder(c)}</span>
          </td>
        ))}
      </tr>
      {isExpanded && details.length > 0 && (
        <tr>
          <td colSpan={cols.length + (details.length > 0 ? 3 : 2)} style={{
            padding: '6px 12px 12px',
            background: 'var(--color-bg-secondary)',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {details.map((d, di) => (
                <div key={di} style={{
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  background: 'var(--color-bg)', overflow: 'hidden',
                }}>
                  <div style={{
                    ...captureSectionHeaderStyle,
                    padding: '4px 10px', fontSize: '0.8125rem',
                  }}>
                    <span style={{ fontWeight: 600 }}>
                      {d.form_title || `Detail ${di + 1}`}
                    </span>
                    <span style={captureBadgeStyle}>Detail grid</span>
                  </div>
                  <GridPreviewView form={d} sampleRows={2} />
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ── Multi-tab preview: tab strip + active record's full form ──

function MultiTabPreview({
  header,
  details,
}: {
  header: SqlModel
  details: SqlModel[]
}) {
  const [active, setActive] = useState(0)
  const tabs = useMemo(() => ([
    { label: 'Record 1', status: 'unsaved' as const },
    { label: 'Record 2', status: 'saved' as const },
  ]), [])

  return (
    <div style={captureSectionStyle}>
      <div style={{
        display: 'flex', gap: 4, flexWrap: 'wrap',
        padding: '6px 8px 0',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)',
      }}>
        {tabs.map((t, i) => {
          const dot = t.status === 'unsaved' ? '#f59e0b' : '#16a34a'
          const isActive = active === i
          return (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '5px 10px', fontSize: '0.8425rem',
                border: '1px solid var(--color-border)',
                borderBottom: isActive ? '1px solid var(--color-bg)' : '1px solid var(--color-border)',
                borderRadius: '6px 6px 0 0',
                background: isActive ? 'var(--color-bg)' : 'transparent',
                color: 'var(--color-text)', cursor: 'pointer',
                marginBottom: -1,
                fontWeight: isActive ? 600 : 400,
              }}
            >
              <span style={{
                display: 'inline-block', width: 8, height: 8,
                borderRadius: '50%', background: dot,
              }} />
              {t.label}
            </button>
          )
        })}
        <span style={{
          padding: '5px 10px', fontSize: '0.8425rem',
          fontStyle: 'italic', color: 'var(--color-text-secondary)',
        }}>+ Record</span>
      </div>
      <div style={{ padding: 10 }}>
        <RecordFormBlock header={header} details={details} />
      </div>
    </div>
  )
}

// ── Multi-accordion preview: vertical sections, first open ──

function MultiAccordionPreview({
  header,
  details,
}: {
  header: SqlModel
  details: SqlModel[]
}) {
  const [openIdx, setOpenIdx] = useState<number>(0)
  const items = [
    { label: 'Record 1', status: 'unsaved' as const },
    { label: 'Record 2', status: 'saved' as const },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it, i) => {
        const open = openIdx === i
        const dot = it.status === 'unsaved' ? '#f59e0b' : '#16a34a'
        return (
          <div key={i} style={{
            border: '1px solid var(--color-border)', borderRadius: 6,
            background: 'var(--color-bg)', overflow: 'hidden',
          }}>
            <button
              type="button"
              onClick={() => setOpenIdx(open ? -1 : i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '8px 10px',
                background: 'var(--color-bg-secondary)',
                border: 'none', cursor: 'pointer',
                textAlign: 'left', color: 'var(--color-text)',
                fontSize: '0.9125rem',
              }}
            >
              <span style={{ width: 12, color: 'var(--color-text-secondary)' }}>
                {open ? '▾' : '▸'}
              </span>
              <span style={{
                display: 'inline-block', width: 8, height: 8,
                borderRadius: '50%', background: dot,
              }} />
              <span style={{ fontWeight: 600 }}>{it.label}</span>
            </button>
            {open && (
              <div style={{ padding: 10 }}>
                <RecordFormBlock header={header} details={details} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Reusable: header form + detail grids stack used by multi-tab /
// multi-accordion previews.
function RecordFormBlock({
  header,
  details,
}: {
  header: SqlModel
  details: SqlModel[]
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{
        border: '1px solid var(--color-border)', borderRadius: 6,
        background: 'var(--color-bg)', overflow: 'hidden',
      }}>
        <div style={{ ...captureSectionHeaderStyle, padding: '4px 10px', fontSize: '0.8125rem' }}>
          <span style={{ fontWeight: 600 }}>{header.form_title || 'Header'}</span>
          <span style={captureBadgeStyle}>Header form</span>
        </div>
        <FormPreviewView form={header} />
      </div>
      {details.map((d, i) => (
        <div key={i} style={{
          border: '1px solid var(--color-border)', borderRadius: 6,
          background: 'var(--color-bg)', overflow: 'hidden',
        }}>
          <div style={{ ...captureSectionHeaderStyle, padding: '4px 10px', fontSize: '0.8125rem' }}>
            <span style={{ fontWeight: 600 }}>{d.form_title || `Detail ${i + 1}`}</span>
            <span style={captureBadgeStyle}>Detail grid</span>
          </div>
          <GridPreviewView form={d} sampleRows={2} />
        </div>
      ))}
    </div>
  )
}

function previewPlaceholder(col: ColumnDef): string {
  const h = (col.heading || col.name || '').toLowerCase()
  if (col.col_type === 'NUMBER') return '—'
  if (col.col_type === 'DATE' || col.col_type === 'DATETIME') return 'YYYY-MM-DD'
  if (col.col_type === 'BOOLEAN') return '☐'
  if (h.includes('email')) return 'user@…'
  return '—'
}

const previewTableStyle: React.CSSProperties = {
  // tableLayout: fixed — without it, colgroup widths are advisory and
  // the browser may stretch/shrink columns to fit content. width:auto
  // (no 100%) sizes the table to the sum of colgroup widths so the
  // editor's per-column widths render exactly.
  tableLayout: 'fixed',
  width: 'auto',
  borderCollapse: 'collapse', fontSize: '0.8425rem',
}
const previewThStyle: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px',
  background: 'var(--color-bg-secondary)',
  borderBottom: '1px solid var(--color-border)',
  color: 'var(--color-text)', fontWeight: 600,
  whiteSpace: 'nowrap',
  // globals.css applies `table th { text-transform: uppercase;
  // letter-spacing: 0.03em }` to every <th> in the app — opt out so
  // designer-authored heading case is preserved here.
  textTransform: 'none',
  letterSpacing: 0,
  fontSize: '0.8425rem',
}
const previewTdStyle: React.CSSProperties = {
  padding: '5px 8px',
  borderBottom: '1px solid var(--color-border)',
  verticalAlign: 'top',
}
const previewCellStyle: React.CSSProperties = {
  // Block-level so the placeholder fills the column width — matches
  // what the runtime renders (a real <input> with width: 100%).
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  padding: '4px 6px',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  color: 'var(--color-text-secondary)',
  fontSize: '0.8125rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const captureSectionStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  overflow: 'hidden',
  background: 'var(--color-bg)',
}

const captureSectionHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '6px 10px',
  background: 'var(--color-bg-secondary)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: '0.8425rem',
  color: 'var(--color-text)',
}

const captureBadgeStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  padding: '1px 8px',
  borderRadius: 12,
  background: 'rgba(245,158,11,0.12)',
  color: '#b45309',
  fontWeight: 600,
}
